import { Box, Step, StepLabel, Stepper, StepConnector } from "@mui/material";
import { styled } from "@mui/material/styles";
import { stepConnectorClasses } from "@mui/material/StepConnector";
import { motion, useInView } from "framer-motion";
import { useRef, type RefObject } from "react";
import bookIcon from "../../assets/we-care-your-journey/book-icon.svg";
import sessionsIcon from "../../assets/we-care-your-journey/sessions-icon.svg";
import growthIcon from "../../assets/we-care-your-journey/growth-icon.svg";
import resourcesIcon from "../../assets/we-care-your-journey/resources-icon.svg";
import sparklingStar from "../../assets/get-started-see-results/sparkling-star.svg";
import tinyStar from "../../assets/get-started-see-results/tiny-star.svg";
import iphoneStar from "../../assets/we-care-your-journey/iphone-star.png";
import { useTranslation } from "react-i18next";
import { useLocal } from "hooks/useLocal";
import { ArrowForward } from "@mui/icons-material";
import downloadAppleStore from "../../assets/download-store.svg";
import { APP_STORE_URL } from "constants/index";
import { detectDevice, launchAppOrFallback } from "utils";

const JourneyConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 40 },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: "transparent",
    borderTopWidth: 0,
  },
}));

function AnimatedLine({
  isInView,
  delay,
  isRTL,
}: {
  isInView: boolean;
  delay: number;
  isRTL?: boolean;
}) {
  return (
    <motion.div
      style={{
        position: "absolute",
        top: 40,
        left: isRTL ? "calc(50% + 44px)" : "calc(-50% + 44px)",
        right: isRTL ? "calc(-50% + 44px)" : "calc(50% + 44px)",
        height: 2,
        zIndex: 0,
        backgroundColor: "#C2C2E7",
        transformOrigin: isRTL ? "right center" : "left center",
      }}
      initial={{ scaleX: 0 }}
      animate={{ scaleX: isInView ? 1 : 0 }}
      transition={{ duration: 0.65, delay, ease: "easeInOut" }}
    />
  );
}

function JourneyStepIcon({
  icon,
  badge,
  isInView,
  delay,
  isRTL,
}: {
  icon: string;
  badge: number;
  isInView: boolean;
  delay: number;
  isRTL?: boolean;
}) {
  return (
    <motion.div
      className="relative flex justify-center items-center w-full z-10"
      initial={{ scale: 0.4, opacity: 0 }}
      animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0.4, opacity: 0 }}
      transition={{ type: "spring", stiffness: 280, damping: 20, delay }}
    >
      <img
        src={icon}
        alt="Journey step"
        className="w-[80px] h-[80px] object-contain"
      />
      <div
        className={`absolute -top-1 ${isRTL ? "left-1" : "right-1"} w-6 h-6 bg-[#4242CE] rounded-full text-white text-xs flex justify-center items-center font-bold`}
      >
        {badge}
      </div>
    </motion.div>
  );
}

function JourneyStepText({
  title,
  description,
  isInView,
  delay,
}: {
  title: string;
  description: React.ReactNode;
  isInView: boolean;
  delay: number;
}) {
  return (
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
      transition={{ duration: 0.45, delay, ease: "easeOut" }}
    >
      <p className="text-[20px] font-semibold text-[#282828] text-nowrap">
        {title}
      </p>
      <p className="text-[#282828] text-sm leading-4.25 mt-4 text-center max-w-[200px]">
        {description}
      </p>
    </motion.div>
  );
}

// ─── Mobile step card ─────────────────────────────────────────────────────────
function JourneyStep({
  stepNumber,
  title,
  description,
  icon,
  isInView,
  delay,
  isRTL,
}: {
  stepNumber: number;
  title: string;
  description: React.ReactNode;
  icon: string;
  isInView: boolean;
  delay: number;
  isRTL?: boolean;
}) {
  return (
    <article className="flex flex-col items-center max-w-[280px] px-2 py-3">
      <motion.div
        className="relative rounded-full mb-5"
        initial={{ scale: 0.4, opacity: 0 }}
        animate={
          isInView ? { scale: 1, opacity: 1 } : { scale: 0.4, opacity: 0 }
        }
        transition={{ type: "spring", stiffness: 280, damping: 20, delay }}
      >
        <img
          src={icon}
          alt="Journey step"
          className="w-[80px] h-[80px] object-contain"
        />
        <div
          className={`absolute -top-1 ${isRTL ? "left-1" : "right-1"} w-6 h-6 bg-[#4242CE] rounded-full text-white text-xs flex justify-center items-center font-bold`}
        >
          {stepNumber}
        </div>
      </motion.div>
      <JourneyStepText
        title={title}
        description={description}
        isInView={isInView}
        delay={delay + 0.15}
      />
    </article>
  );
}

function JourneyStepper({
  steps,
  isMobile = false,
  isRTL = false,
}: {
  steps: { title: string; description: React.ReactNode; icon: string }[];
  isMobile?: boolean;
  isRTL?: boolean;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const iconDelay = (i: number) => i * 0.6;
  const lineDelay = (i: number) => i * 0.6 + 0.35;
  const textDelay = (i: number) => i * 0.6 + 0.15;

  if (isMobile) {
    return (
      <section
        ref={ref}
        className="flex flex-col items-center justify-center gap-6"
      >
        {steps.map((step, i) => (
          <JourneyStep
            key={i}
            stepNumber={i + 1}
            title={step.title}
            description={step.description}
            icon={step.icon as string}
            isInView={isInView}
            delay={iconDelay(i)}
            isRTL={isRTL}
          />
        ))}
      </section>
    );
  }

  return (
    <Box ref={ref} sx={{ width: "100%" }}>
      <Stepper
        activeStep={steps.length}
        alternativeLabel
        connector={<JourneyConnector />}
        sx={{
          "& .MuiStep-root": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          },
        }}
      >
        {steps.map((step, i) => (
          <Step key={i} completed>
            {i > 0 && (
              <AnimatedLine
                isInView={isInView}
                delay={lineDelay(i - 1)}
                isRTL={isRTL}
              />
            )}
            <StepLabel
              StepIconComponent={() => (
                <JourneyStepIcon
                  icon={step.icon as string}
                  badge={i + 1}
                  isInView={isInView}
                  delay={iconDelay(i)}
                  isRTL={isRTL}
                />
              )}
            >
              <article className="flex flex-col items-center max-w-[251px]">
                <JourneyStepText
                  title={step.title}
                  description={step.description}
                  isInView={isInView}
                  delay={textDelay(i)}
                />
              </article>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}

// ─── Section export ───────────────────────────────────────────────────────────
type GetStartedSeeResultsProps = {
  journeySectionRef?: RefObject<HTMLElement>;
};

export function GetStartedSeeResults({
  journeySectionRef,
}: GetStartedSeeResultsProps) {
  const { t } = useTranslation(["common"]);
  const { direction, isArabic } = useLocal();
  const dirAttr: string | undefined = Array.isArray(direction)
    ? direction[0]
    : direction;

  const isRTL = dirAttr === "rtl";

  const handleAppleStoreClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    const { platform } = detectDevice();

    if (platform !== "ios" && platform !== "android") {
      return;
    }

    event.preventDefault();

    launchAppOrFallback({
      deepLink: "numuw://",
      fallbackUrl: APP_STORE_URL,
    });
  };

  const screeningRaw = t(
    "getStartedSection.screeningToolAdvisoryCallCarePlanMatch",
  );

  const renderWithIcons = (str: string) => {
    const parts = str.split("{{icon}}");
    return (
      <>
        {parts.map((part, idx) => (
          <span key={idx}>
            {part}
            {idx < parts.length - 1 && <ArrowForward fontSize="small" />}
          </span>
        ))}
      </>
    );
  };

  const steps = [
    {
      title: t("getStartedSection.aiOnboarding"),
      description: renderWithIcons(screeningRaw),
      icon: bookIcon,
    },
    {
      title: t("getStartedSection.growth"),
      description: t("getStartedSection.seeYourChildsProgress"),
      icon: growthIcon,
    },
    {
      title: t("getStartedSection.sessions"),
      description: t("getStartedSection.oneToOneConsultations"),
      icon: sessionsIcon,
    },
    {
      title: t("getStartedSection.resources"),
      description: t("getStartedSection.fullAccessToLibrary"),
      icon: resourcesIcon,
    },
  ];

  return (
    <section
      dir={dirAttr}
      className={`bg-[#ffffff] ${isArabic ? "font-ibmplex" : "font-inter"}`}
    >
      <article className="relative py-20 px-6 max-w-[1400px] mx-auto">
        <img
          src={sparklingStar}
          alt={t("getStartedSection.sparklingStar") ?? "Sparkling Star"}
          className={`max-md:max-w-[130px] absolute top-48 md:top-12 ${isRTL ? "right-1/12" : "left-1/12"}`}
        />
        <div className="flex flex-col items-center justify-center gap-2">
          <p
            className={`text-[16px] text-center font-bold text-[#4242CE] ${isArabic ? "font-ibmplex" : "font-funnel"}`}
          >
            {t("getStartedSection.getStartedSeeResults")}
          </p>
          <div
            className={`bg-[#4242CE] h-0.5 ${isArabic ? "max-w-[140px]" : "max-w-[76px]"} w-full mt-1`}
          ></div>
        </div>
        <p
          className={`text-center text-2xl md:text-[32px] font-bold text-gray leading-[39px] mt-[18px] ${isArabic ? "font-ibmplex" : "font-funnel"}`}
        >
          {t("getStartedSection.howWeGuideYourCareJourney")}
        </p>
        <section className="mt-20">
          <div className="hidden md:block">
            <JourneyStepper steps={steps} isRTL={isRTL} />
          </div>
          <div className="md:hidden">
            <JourneyStepper steps={steps} isMobile isRTL={isRTL} />
          </div>
        </section>
      </article>

      {/* Your family deserves… */}
      <article
        className="py-20 px-6 max-w-[1400px] mx-auto"
        data-journey-download-section="true"
        ref={journeySectionRef}
      >
        <article className="rounded-2xl bg-[#4242CE] py-8 px-10 md:pl-[130px] flex-col lg:flex-row flex max-lg:min-h-[565px] lg:h-[333px] max-[1327px]:h-auto max-[1327px]:pb-12 justify-between items-center relative">
          <section className="flex flex-col gap-[13px] max-w-[600px] relative z-50">
            <img
              src={tinyStar}
              alt={t("getStartedSection.tinyStar") ?? "Tiny Star"}
              className={`w-[41px] h-[36px]`}
            />
            <p className="text-white text-[32px] lg:text-[40px] font-bold leading-12">
              {t("getStartedSection.yourFamilyDeservesRealCare")}
            </p>
            <p className="text-white text-sm lg:text-[16px] leading-4.75">
              {t("getStartedSection.downloadOurApp")}
            </p>
            <section className="flex gap-4 relative z-50">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleAppleStoreClick}
                aria-label="Download on the App Store"
                className="transition-transform duration-300 ease-in-out hover:scale-105"
              >
                <img
                  src={downloadAppleStore}
                  alt="download numuw on apple store"
                />
              </a>
              {/* <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
                className="transition-transform duration-300 ease-in-out hover:scale-105"
              >
                <img
                  src={downloadPlayStore}
                  alt="download numuw on play store"
                />
              </a> */}
            </section>
          </section>
          <img
            src={iphoneStar}
            className={`max-w-none w-[450px] lg:max-w-[560px] lg:w-auto lg:h-auto absolute -bottom-12 z-10 max-[1327px]:static max-[1327px]:mt-6 max-[1327px]:w-[360px] max-[1327px]:self-center ${
              isRTL
                ? "right-0 lg:left-40 max-lg:-bottom-[45px] lg:right-auto"
                : "right-0 max-lg:-bottom-[45px] lg:right-40 lg:left-auto"
            }`}
            alt="iPhone with star"
          />
        </article>
      </article>
    </section>
  );
}
